import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import BodyText from 'LuxKit/Typography/BodyText'
import type { ComponentProps, MouseEventHandler } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import _PriceRowLoyaltyPointsContent from './_PriceRowLoyaltyPointsContent'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL } from 'loyalty/constants/loyaltyBonusPoints'
import { rem } from 'polished'
import styled from 'styled-components'

interface Props extends Omit<ComponentProps<typeof _PriceRowLoyaltyPointsContent>, 'horizontalAlign'> {
  tier: App.LoyaltyTier
  campaigns: Array<App.LoyaltyBonusPointsCampaignType>
  size: 'S' | 'M'
  /**
   * @default start
   */
  horizontalAlign?: 'start' | 'center'
  onPillClick?: MouseEventHandler<HTMLButtonElement>
  hideTierIcon?: boolean
}

export const SIZES_BODY_TEXT_VARIANTS: Record<Props['size'], ComponentProps<typeof BodyText>['variant']> = {
  S: 'small',
  M: 'medium',
}

const TIER_BODY_TEXT_COLOURS: Record<App.LoyaltyTier, App.Design.ContentColour> = {
  bronze: 'tier-bronze-dark',
  silver: 'tier-silver-dark',
  gold: 'tier-gold-dark',
  platinum: 'tier-platinum-dark',
  infinite: 'tier-infinite-dark',
}

const LayoutContainer = styled.div`
  container-type: inline-size;
  max-width: 100%;
  width: 100%;
`

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: ${rem(6)};
  column-gap: ${rem(8)};
  max-width: 100%;
  min-width: 0;

  &.h-align-center {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }

  @container (min-width: ${rem(220)}) {
    &.without-status-credits:not(.h-align-center) {
      flex-direction: row;
      align-items: center;
    }
  }

  @container (min-width: ${rem(330)}) {
    &.has-status-credits:not(.h-align-center) {
      flex-direction: row;
      align-items: center;
    }
  }
`

const Campaigns = styled(Group)`
  flex: 0 0 auto;
  max-width: 100%;
`

const EarnText = styled(BodyText)`
  min-width: 0;
  max-width: 100%;
`

function PriceRowLoyaltyPointsPromoPill({
  tier,
  size,
  campaigns,
  earnablePoints,
  statusCredits,
  calculationType,
  pointsEarnRatio,
  horizontalAlign = 'start',
  trailingCaption,
  onInfoIconClick,
  onPillClick,
  hideTierIcon = false,
}: Props) {
  const intl = useIntl()
  const handlePillClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onPillClick?.(event)
  }, [onPillClick])
  const hasVisibleStatusCredits = calculationType !== 'nominal' && !!statusCredits
  const layoutClassName = hasVisibleStatusCredits ?
    `h-align-${horizontalAlign} has-status-credits` :
    `h-align-${horizontalAlign} without-status-credits`

  return <LayoutContainer>
    <Layout className={layoutClassName}>
      <Campaigns
        direction="horizontal"
        gap={8}
        horizontalAlign={horizontalAlign}
        wrap="wrap"
      >
        {campaigns.map(campaign => (
          <Clickable key={campaign} onClick={onPillClick ? handlePillClick : undefined}>
            <LoyaltyTierPill kind="primary" tier={tier} icon={hideTierIcon ? null : undefined}>
              {intl.formatMessage(LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[campaign])}
            </LoyaltyTierPill>
          </Clickable>
        ))}
      </Campaigns>
      <EarnText
        variant={SIZES_BODY_TEXT_VARIANTS[size]}
        weight="bold"
        colour={TIER_BODY_TEXT_COLOURS[tier]}
        align={horizontalAlign}
      >
        <_PriceRowLoyaltyPointsContent
          earnablePoints={earnablePoints}
          pointsEarnRatio={pointsEarnRatio}
          statusCredits={statusCredits}
          onInfoIconClick={onInfoIconClick}
          trailingCaption={trailingCaption}
          calculationType={calculationType}
          horizontalAlign={horizontalAlign}
        />
      </EarnText>
    </Layout>
  </LayoutContainer>
}

export default PriceRowLoyaltyPointsPromoPill
